import React, { useEffect, useRef, useState } from "react";
import { parseDateOnly, presentDateOnly } from "../../../utils/dateOnly";

interface Props {
  value: string; // 'YYYY-MM-DD' or ''
  onChange: (val: string) => void;
  label?: string;
  placeholder?: string;
  style?: React.CSSProperties;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/**
 * SharedDatePicker — the single premium date-picker used app-wide.
 * Renders a compact trigger + a floating calendar popup. No external
 * dependency. Value/format is always ISO "YYYY-MM-DD".
 */
const PremiumDatePicker: React.FC<Props> = ({
  value, onChange, label, placeholder, style,
}) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState<number>(() =>
    value ? parseInt(value.split("-")[0]) : new Date().getFullYear()
  );
  const [viewMonth, setViewMonth] = useState<number>(() =>
    value ? parseInt(value.split("-")[1]) - 1 : new Date().getMonth()
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (value) {
      setViewYear(parseInt(value.split("-")[0]));
      setViewMonth(parseInt(value.split("-")[1]) - 1);
    }
  }, [value]);

  // Anchor at LOCAL midnight, never UTC — see utils/dateOnly for why.
  const selected = parseDateOnly(value);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const selectDay = (d: number) => {
    const m = String(viewMonth + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    onChange(`${viewYear}-${m}-${dd}`);
    setOpen(false);
  };

  const isSelected = (d: number) =>
    !!selected &&
    selected.getFullYear() === viewYear &&
    selected.getMonth() === viewMonth &&
    selected.getDate() === d;

  const isToday = (d: number) =>
    today.getFullYear() === viewYear &&
    today.getMonth() === viewMonth &&
    today.getDate() === d;

  const displayValue = presentDateOnly(value);

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      {label && (
        <div style={{
          fontSize: 11, fontWeight: 700, color: "#64748b",
          textTransform: "uppercase", letterSpacing: "0.6px",
          marginBottom: 6,
        }}>{label}</div>
      )}

      {/* Trigger */}
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", background: "#fff",
          border: open ? "2px solid #1b3a6b" : "1.5px solid #e2e8f0",
          borderRadius: 10, cursor: "pointer",
          boxShadow: open ? "0 0 0 3px rgba(27,58,107,0.1)" : "none",
          transition: "all 0.15s", minWidth: 160, userSelect: "none",
        }}
      >
        <span style={{ fontSize: 15 }}>📅</span>
        <span style={{
          flex: 1, fontSize: 13, fontWeight: 500,
          color: displayValue ? "#1a1a2e" : "#94a3b8",
        }}>
          {displayValue || placeholder || "Select date"}
        </span>
        <span style={{
          fontSize: 10, color: "#94a3b8",
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }}>▼</span>
      </div>

      {/* Calendar popup */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 9999,
          background: "#fff", borderRadius: 16, padding: 16,
          boxShadow: "0 16px 48px rgba(0,0,0,0.16)",
          border: "1px solid #e2e8f0", minWidth: 280,
        }}>
          {/* Month/Year nav */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", marginBottom: 14,
          }}>
            <button
              onClick={() => {
                if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
                else setViewMonth((m) => m - 1);
              }}
              style={{
                background: "#f1f5f9", border: "none", width: 32, height: 32,
                borderRadius: 8, cursor: "pointer", fontSize: 16,
                color: "#1b3a6b", fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >‹</button>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1b3a6b" }}>
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <button
              onClick={() => {
                if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
                else setViewMonth((m) => m + 1);
              }}
              style={{
                background: "#f1f5f9", border: "none", width: 32, height: 32,
                borderRadius: 8, cursor: "pointer", fontSize: 16,
                color: "#1b3a6b", fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >›</button>
          </div>

          {/* Day headers */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2, marginBottom: 6,
          }}>
            {DAYS.map((d) => (
              <div key={d} style={{
                textAlign: "center", fontSize: 11, fontWeight: 700,
                color: "#94a3b8", padding: "3px 0",
              }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2,
          }}>
            {Array.from({ length: firstDay }).map((_, i) => (<div key={`e${i}`} />))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const d = i + 1;
              const sel = isSelected(d);
              const tod = isToday(d);
              return (
                <button
                  key={d}
                  onClick={() => selectDay(d)}
                  onMouseEnter={(e) => {
                    if (!sel) (e.currentTarget as HTMLElement).style.background = "#f0f4ff";
                  }}
                  onMouseLeave={(e) => {
                    if (!sel) (e.currentTarget as HTMLElement).style.background = "transparent";
                  }}
                  style={{
                    border: tod && !sel ? "2px solid #1b3a6b" : "none",
                    borderRadius: 8, padding: "6px 0",
                    fontSize: 13, cursor: "pointer",
                    fontWeight: sel || tod ? 700 : 400,
                    background: sel ? "#1b3a6b" : "transparent",
                    color: sel ? "#fff" : tod ? "#1b3a6b" : "#1a1a2e",
                    transition: "all 0.1s",
                  }}
                >{d}</button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            marginTop: 12, paddingTop: 12,
            borderTop: "1px solid #f1f5f9",
          }}>
            <button
              onClick={() => { onChange(""); setOpen(false); }}
              style={{
                background: "none", border: "none",
                color: "#94a3b8", fontSize: 12,
                cursor: "pointer", fontWeight: 600,
              }}
            >Clear</button>
            <button
              onClick={() => {
                const t = new Date();
                const m = String(t.getMonth() + 1).padStart(2, "0");
                const d = String(t.getDate()).padStart(2, "0");
                onChange(`${t.getFullYear()}-${m}-${d}`);
                setOpen(false);
              }}
              style={{
                background: "#1b3a6b", color: "#fff", border: "none",
                borderRadius: 8, padding: "6px 16px",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}
            >Today</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumDatePicker;
